import React from "react";
import { useQuery } from "@apollo/client/react";
import { type Color, type IconName } from "@salestrak/ui";

import {
  IntegrationCatalogDocument,
  type IntegrationCategoryRow,
  type IntegrationProviderRow,
} from "#app/lib/data";

import {
  type IntegrationCategory,
  type IntegrationProvider,
} from "./integrations-data";

import { BRAND_LOGOS } from "./brand-logos";

// The query returns `icon`/`color` as plain strings (the API can't reference
// the library's unions). These adapters cast them back to the view types and
// convert `null → undefined`.
const toProvider = (p: IntegrationProviderRow): IntegrationProvider => ({
  id: p.id,
  name: p.name,
  description: p.description,
  icon: p.icon as IconName,
  color: p.color as Color,
  logo: BRAND_LOGOS[p.id],
  kind: p.kind,
  footnote: p.footnote ?? undefined,
  categoryIds: p.categoryIds,
  featured: p.featured ?? undefined,
  oauth: p.oauth,
  setup: p.setup,
  // `command` is normalized like every other nullable — the view types spell
  // absence as `undefined`, and NullToUndefined recurses into this one.
  remoteCollections: p.remoteCollections.map((c) => ({
    ...c,
    description: c.description ?? undefined,
  })),
  featuresLabel: p.featuresLabel ?? undefined,
  connectionGuide: p.connectionGuide
    ? { ...p.connectionGuide, command: p.connectionGuide.command ?? undefined }
    : undefined,
  authFields: p.authFields.map((f) => ({
    key: f.key,
    label: f.label,
    sublabel: f.sublabel ?? undefined,
    secret: f.secret,
    placeholder: f.placeholder ?? undefined,
    required: f.required,
    group: f.group ?? undefined,
    control: (f.control ?? undefined) as "text" | "textarea" | "list" | undefined,
    listAddLabel: f.listAddLabel ?? undefined,
  })),
  connections: p.connections.map((c) => ({
    id: c.id,
    name: c.name,
    active: c.active,
    needsAttention: c.needsAttention,
    attentionMessage: c.attentionMessage ?? undefined,
    webhookUrl: c.webhookUrl ?? undefined,
  })),
});

const toCategory = (c: IntegrationCategoryRow): IntegrationCategory => ({
  id: c.id,
  label: c.label,
  icon: c.icon as IconName,
});

export type IntegrationCatalogState = {
  categories: IntegrationCategory[];
  providers: IntegrationProvider[];
  /** `loading && !data` — true only before the first response. */
  initialLoading: boolean;
};

/** Fetch the integration marketplace (categories + providers) in one
 * request. `skip` lets the dialog avoid fetching until it's opened. */
export function useIntegrationCatalog({
  skip = false,
}: { skip?: boolean } = {}): IntegrationCatalogState {
  const { data, loading } = useQuery(IntegrationCatalogDocument, {
    fetchPolicy: "cache-and-network",
    skip,
  });
  const initialLoading = loading && !data;

  return React.useMemo<IntegrationCatalogState>(() => {
    const c = data?.integrationCatalog;
    return {
      categories: c ? c.categories.map(toCategory) : [],
      providers: c ? c.providers.map(toProvider) : [],
      initialLoading,
    };
  }, [data, initialLoading]);
}
